import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import copy from 'copy-to-clipboard';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../common/types';
import styles from './SMLCopyLinkButton.module.scss';
import { trackEvent } from '../../analytics/sml/analytics';

interface SMLCopyLinkButtonProps {
  shareUrl: string;
}

const COPIED_MS = 1600;

/**
 * The most-used control on the screen, and now the darkest thing in the bar.
 *
 * It answers in place: the label turns green and reads "Copied" for a moment,
 * so the artist knows the clipboard changed without a toast telling them.
 */
const SMLCopyLinkButtonComponent: React.FC<SMLCopyLinkButtonProps> = ({ shareUrl }) => {
  const intl = useIntl();
  const [isCopied, setIsCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  const onCopy = useCallback(() => {
    copy(shareUrl);
    setIsCopied(true);
    trackEvent('link_copied', { kind: 'smartlink' });
    if (timer.current !== null) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => setIsCopied(false), COPIED_MS);
  }, [shareUrl]);

  return (
    <button type="button" className={clsx(styles.copy, isCopied && styles.copied)} onClick={onCopy}>
      <FontAwesomeIcon icon={isCopied ? faCheck : faLink} />
      {isCopied
        ? intl.formatMessage({ id: 'app.sml.details.copied', defaultMessage: 'Copied' })
        : intl.formatMessage({ id: 'app.sml.details.copy', defaultMessage: 'Copy link' })}
    </button>
  );
};

export const SMLCopyLinkButton = TypedMemo(SMLCopyLinkButtonComponent);
